import * as React from "react"
import {
  List,
  ListItem,
  ListItemActions,
  ListItemContent,
} from "@modules/ui/components/List"
import {
  Badge,
  Button,
  Spacer,
  Text,
  ThemeCss,
  VisuallyHidden,
} from "gatsby-interface"
import { MdArrowForward } from "react-icons/md"
import { Integration } from "@modules/ui/components/Integration"
import { logDrainIntegration as text } from "@modules/locales/default.js"
import { interpolateMessage } from "@modules/locales"
import { logDrainVendorLogo, logDrainVendorNames } from "../constants"
import { LogDrainIntegration, LogDrainProvider } from "@modules/graphql/types"
import { isProperlyConnected } from "../helpers"
import { LogDrainIntegrationDropdown } from "@modules/logDrain/public-target/LogDrainIntegrationDropdown"
import { getContextuallyFormatedDate } from "@modules/ui/utils/getContextuallyFormatedDate"
import { LogDrainIntegrationError } from "./LogDrainIntegrationList.parts"

export type LogDrainIntegrationsListProps = {
  integrations?: LogDrainIntegration[]
  onSelect: (cdnIntegration: LogDrainIntegration) => void
  onDelete: (cdnIntegration: LogDrainIntegration) => void
  allowToEdit?: boolean
}

const getLastDrainedAtDateString = (lastDrainedAt?: string) => {
  let dateString = getContextuallyFormatedDate(lastDrainedAt)

  if (!dateString) {
    return null
  }

  dateString = dateString.replace("Today", "today")
  dateString = dateString.replace("Yesterday", "yesterday")

  return dateString
}

export function LogDrainIntegrationsList({
  integrations = [
    { logDrainProvider: LogDrainProvider.Datadog } as LogDrainIntegration,
  ],
  onSelect,
  onDelete,
  allowToEdit,
}: LogDrainIntegrationsListProps) {
  return (
    <List>
      {integrations.map(logDrain => {
        const isConnected = isProperlyConnected(logDrain)
        const lastDrainedAt = getLastDrainedAtDateString(logDrain.lastDrainedAt)
        return (
          <ListItem key={logDrain.logDrainProvider}>
            <ListItemContent>
              <div css={listItemContentCss}>
                <div css={listItemContentColumnCss}>
                  <Integration.Vendor
                    image={logDrainVendorLogo[logDrain.logDrainProvider]}
                    name={logDrain.logDrainProvider}
                  />

                  <VisuallyHidden>
                    {logDrainVendorLogo[logDrain.logDrainProvider]}
                  </VisuallyHidden>

                  {isConnected && (
                    <React.Fragment>
                      <Badge
                        css={logDrainConnectionBadgeCss}
                        tone={logDrain.error ? "DANGER" : "SUCCESS"}
                        data-testid={`${logDrain.logDrainProvider}-status`}
                      >
                        {logDrain.error ? `Not Connected` : `Connected`}
                      </Badge>

                      <Text css={lastDrainedAtCss}>
                        {lastDrainedAt
                          ? interpolateMessage<"lastDrainedAt">(
                              text.messages.lastDrainedAt,
                              {
                                lastDrainedAt,
                              }
                            )
                          : text.messages.neverDrained}
                      </Text>
                    </React.Fragment>
                  )}
                </div>
                {logDrain.error && (
                  <React.Fragment>
                    <Spacer size={5} />
                    <LogDrainIntegrationError>
                      {logDrain.error}
                    </LogDrainIntegrationError>
                  </React.Fragment>
                )}
              </div>
            </ListItemContent>

            <ListItemActions>
              <div css={listItemActionsCss}>
                {isConnected && allowToEdit && (
                  <LogDrainIntegrationDropdown
                    data-testid={`${logDrain.logDrainProvider}-edit-dropdown`}
                    onEdit={() => onSelect(logDrain)}
                    onDelete={() => onDelete(logDrain)}
                    aria-label={interpolateMessage<"logDrainProvider">(
                      text.actions.connectProvider,
                      {
                        logDrainProvider:
                          logDrainVendorNames[logDrain.logDrainProvider],
                      }
                    )}
                  />
                )}

                {!isConnected && allowToEdit && (
                  <Button
                    variant="GHOST"
                    rightIcon={<MdArrowForward />}
                    onClick={() => onSelect(logDrain)}
                    aria-label={interpolateMessage<"logDrainProvider">(
                      text.actions.connect,
                      {
                        logDrainProvider:
                          logDrainVendorNames[logDrain.logDrainProvider],
                      }
                    )}
                  >
                    {text.actions.connect}
                  </Button>
                )}
              </div>
            </ListItemActions>
          </ListItem>
        )
      })}
    </List>
  )
}

/* Styles */
const listItemContentCss: ThemeCss = theme => ({
  paddingTop: `${theme.space[5]}`,
})

const listItemContentColumnCss: ThemeCss = theme => ({
  display: "flex",
  flexDirection: "column",

  [theme.mediaQueries.phablet]: {
    flexDirection: "row",
  },
})

const listItemActionsCss: ThemeCss = theme => ({
  paddingTop: `${theme.space[5]}`,
})

const lastDrainedAtCss: ThemeCss = theme => ({
  marginLeft: theme.space[4],
  fontSize: theme.fontSizes[0],
  display: `flex`,
  alignItems: `center`,
})

const logDrainConnectionBadgeCss: ThemeCss = theme => ({
  marginLeft: theme.space[5],
  justifyContent: "center",
  display: "flex",
})
